/* eslint-disable @spaced-out/i18n/no-static-labels */
// @ts-nocheck
import {connect} from 'react-redux';
import {withI18n} from 'src/utils/language-support';
import type {EventTemplate} from 'src/api-parsers/events';
import {IndexStore} from 'src/stores/index';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import unescape from 'lodash/unescape';

import FluxComponent from 'src/flux/component';
import Loadable from 'src/components/lib/loadable';
import Loading from 'src/components/lib/loading/loading';
//import CodeEditor from 'src/components/lib/code-editor';

import {updateEventTemplate} from 'src/actions/event-template';
import {selectCorporateLabels} from 'src/selectors/language-support';
import I18nContext from 'src/contexts/i18n-context';

import css from './edit.css';


const mapStateToProps = (state: unknown) => ({
  corporateLabels: selectCorporateLabels(state),
});

type EditorProps = {
  template: EventTemplate;
  store: IndexStore;
  router: any; // typeof router?;
  corporateLabels: {
    [key: string]: string;
  };
  t: (key: string, value: string) => string;
  makeKey: (key: string) => string;
  fluxRef?: (component?: any | null | undefined) => void;
};
type EditorState = {
  disabled: boolean;
  template: EventTemplate;
};
class TemplateEditor extends React.Component<EditorProps, EditorState> {
  static contextType = I18nContext;
  constructor(props: unknown) {
    super(props);

    this.state = {
      disabled: false,
      template: this.props.template,
    };

    (this as any).handleFormUpdate = this.handleFormUpdate.bind(this);
    (this as any).handleSave = this.handleSave.bind(this);
  }

  componentDidMount() {
    if (this.props.fluxRef) {
      this.props.fluxRef(this);
    }
  }

  componentWillUnmount() {
    if (this.props.fluxRef) {
      this.props.fluxRef(null);
    }
  }

  render() {
    const {template} = this.state;
    const {corporateLabels, t, makeKey} = this.props;

    // This is very conservative, but possible if somebody (not the template listing page)
    // routes to this page somehow. There isn't a case where that happens right now, but
    // this is here to be safe all the same.
    if (!template) {
      return <Loading />;
    }

    const {html, debugEventHtml, name} = template;

    const templateContent = html === null ? unescape(debugEventHtml) : html;

    return (
      <SimpleTemplateEditor
        html={templateContent}
        onHTMLChange={this.handleFormUpdate('html')}
        name={name}
        onNameChange={this.handleFormUpdate('name')}
        onSave={this.handleSave}
        disabled={this.state.disabled}
        readOnly={template.templateType === 'builtin'}
        templateId={template.id}
        templateNameLabel={t(
          makeKey(corporateLabels['template_name']),
          'Template Name',
        )}
        emailTemplatesLabel={t(
          makeKey(corporateLabels['sense_templates']),
          'Sense templates',
        )}
      />
    );
  }

  handleFormUpdate(key: unknown) {
    return (newVal: string) => {
      this.setState({
        template: {
          ...this.state.template,
          [key]: newVal,
        },
      });
    };
  }

  getTemplate() {
    return this.state.template;
  }

  handleSave() {
    const {template} = this.props;
    this.setState({disabled: true});
    updateEventTemplate(this.props.store, this.props.template)
      .then(
        () => {
          this.setState({disabled: false});
          this.props.router.push(`/settings/templates/${template.id}/`);
        },
        () => {
          // If we get an error, re-enabled the form
          this.setState({disabled: false});
        },
      )
      .catch(() => {
        // catch an error more significantly here
      });
  }
}

export default connect(mapStateToProps)(
  FluxComponent(withI18n(TemplateEditor)),
);

type SimpleEditorProps = {
  onNameChange: (val: string) => void;
  onHTMLChange: (val: string) => void;
  onSave: () => void;
  html: string;
  name: string;
  disabled: boolean;
  readOnly: boolean;
  templateId: string;
};
export const SimpleTemplateEditor = (props: SimpleEditorProps) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;

  return (
    <div className={css.container}>
      <div className={css.notes}>
        <div className={css.notesInset}>
          <div className={css.formGroup}>
            <h4 className={css.heading}>{props.templateNameLabel}</h4>
            <input
              disabled={props.disabled}
              className={css.textInput}
              onChange={(evt) => props.onNameChange(evt.target.value)}
              type="text"
              value={props.name}
            />
          </div>

          <ul className={css.templateNotes}>
            <li>
              {props.emailTemplatesLabel}
              {` ${t('require_the', 'require the')}`}{' '}
              <span className={css.variableName}>{'{{content}}'}</span>{' '}
              {`${t('variable', 'variable')}.`}
            </li>
            {/*
          (nilarnab) commenting this out for now
          <li>
            <span className={css.variableName}>{'{{unsubscribe}}'}</span> will
            include an html snippet with an unsubscribe link.
          </li>
          <li>
            <span className={css.variableName}>
              {'<%asm_global_unsubscribe_raw_url%>'}
            </span>{' '}
            will be replaced with the raw url to an unsubscribe link.
          </li> */}
            <li>
              <span className={css.variableName}>{'{{survey_css}}'}</span>
              {` ${t(
                'will_include_raw_css_needed_to_properly_style_survey_controls_in_emails_You_need_to_place_this_in_the_template\'s',
                'will include raw css needed to properly style survey controls in emails. You need to place this in the template\'s',
              )} `}
              <code>{t('head', 'head')}</code> {`${t('inside_a', 'inside a')} `}
              <code>{t('style', 'style')}</code>
              {` ${t('tag', 'tag')}.`}
            </li>
          </ul>
        </div>
      </div>

      <CodeEditor
        className={props.readOnly ? css.readOnlyEditor : css.editor}
        width="auto"
        height="auto"
        mode="html"
        theme="tomorrow"
        onChange={props.onHTMLChange}
        name="templateeditoromgz"
        value={props.html}
        readOnly={props.readOnly}
      />
    </div>
  );
};

const CodeEditor = Loadable({
  loader: () => import('src/components/lib/code-editor'),
});
